// @ts-nocheck
/* eslint-disable camelcase */
import React, { useEffect, useLayoutEffect } from 'react';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { ThemeProvider as MuiThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Highcharts from 'highcharts';
import 'highcharts/highcharts-more';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/boost';
import 'highcharts/modules/solid-gauge';
import 'highcharts/modules/debugger';
import 'highcharts/modules/pattern-fill';
import 'highcharts/modules/draggable-points';
import 'highcharts/modules/sankey';
import 'highcharts/modules/accessibility';
import { useTranslation } from 'react-i18next';
import SimulationResultsProvider from 'context/SimulationResultsProvider';
import chartOptions from '../../charts/constants/chartOptionConstants';
// to access the history in the HTTP file we need to creat a 'custom' history object which is than passed as a
// history-prop to the Router-Component.
import theme from '../../../theme';
import AppProvider from '../../../context/AppProvider';
import appData from '../../../context/AppProvider/appContextData';
import StructureProvider from '../../../context/StructureProvider';
import UIParameterProvider from '../../../context/UIParameterProvider';
import PlotDataProvider from '../../../context/PlotDataProvider';
import UserDataProvider from '../../../context/UserDataProvider';
import LicenseDataProvider from '../../../context/LicenseDataProvider';
import ModalProvider from '../../../context/ModalProvider';
import vistasansmed from '../../../assets/fonts/vistasansmed-020415006EmigreWebOnly.woff';
import vistasansbook from '../../../assets/fonts/vistasansbook-020415006EmigreWebOnly.woff';
import robotolight from '../../../assets/fonts/Roboto-Light.woff';
import noto from '../../../assets/fonts/noto-sans-v12-latin-regular.woff';
import ambit from '../../../assets/fonts/Ambit-Regular.woff';
import settings from '../../../settings';
import { useTailwindThemeVariables } from '../../../utils/tailwind-theme';
import '../../../styles/tailwind.css';
import { ToastContainer } from '../../common/Toaster';

const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: vistasansmed;
    src: url(${vistasansmed}) format('truetype');
    font-weight: normal;
    font-style: normal;
  }

  @font-face {
    font-family: vistasansbook;
    src: url(${vistasansbook}) format('truetype');
    font-weight: normal;
    font-style: normal;
  }

  @font-face {
    font-family: ambit;
    src: url(${ambit}) format('truetype');
    font-weight: normal;
    font-style: normal;
  }

  @font-face {
    font-family: noto;
    src: url(${noto}) format('truetype');
    font-weight: normal;
    font-style: normal;
  }

  @font-face {
    font-family: robotolight;
    src: url(${robotolight}) format('truetype');
    font-weight: normal;
    font-style: normal;
  }
`;
// Set global options for highcharts. chartOptions are set depending on App
Highcharts.setOptions(chartOptions);
type Props = {
  children: React.ReactNode;
};

// Create QueryClient once outside component to prevent cache resets on re-renders
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      networkMode: 'offlineFirst',
      staleTime: 2 * 60 * 1000, // 2 minutes default
      refetchOnWindowFocus: false, // Optional: disable window focus refetches
    },
  },
});

/**
 * This is the base component that nests all the pages and holds the router.
 * @param
 * @returns {*}
 * @constructor
 */
export const AppComponent = ({ children }: Props) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage(appData.fixedLanguage);
  }, []);

  // Layout effect so the variables exist before the first paint of any loader
  useLayoutEffect(() => {
    useTailwindThemeVariables(theme);
  }, []);
  return (
    <ThemeProvider theme={theme}>
      <StyledEngineProvider injectFirst>
        <MuiThemeProvider theme={theme}>
          <CssBaseline />
          <GlobalStyle />
          <ModalProvider>
            {settings.product === 'pandemic' ? (
              <AppProvider>{children}</AppProvider>
            ) : (
              <AppProvider>
                <QueryClientProvider client={queryClient}>
                  <UserDataProvider>
                    <StructureProvider>
                      <UIParameterProvider>
                        <LicenseDataProvider>
                          <PlotDataProvider>
                            <SimulationResultsProvider>
                              {children}
                              <ToastContainer />
                            </SimulationResultsProvider>
                          </PlotDataProvider>
                        </LicenseDataProvider>
                      </UIParameterProvider>
                    </StructureProvider>
                  </UserDataProvider>
                </QueryClientProvider>
              </AppProvider>
            )}
          </ModalProvider>
        </MuiThemeProvider>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};
export default AppComponent;
